import { useState, useCallback } from 'react'
import { Box, Button, IconButton, InputAdornment } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import type { FieldErrors } from 'react-hook-form'
import { TextFieldElement } from 'react-hook-form-mui'
import { FaEye, FaEyeSlash } from 'react-icons/fa'
import { signupManual } from 'src/utils/auth'

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    '& fieldset': { border: 'none', borderBottom: '1px solid #e2e8f0' },
    '&:hover fieldset': { borderBottom: '1px solid #cbd5e1' },
    '&.Mui-focused fieldset': { border: 'none', borderBottom: '2px solid #4277b9' }
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.9rem',
    color: '#94a3b8',
    '&.Mui-focused': { color: '#4277b9' }
  }
}

type Props = { setError: (msg?: string) => void }

export function SignupForm({ setError }: Props) {
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const formContext = useForm<{ email: string; password: string }>({
    defaultValues: { email: '', password: '' }
  })

  const onRegisterInvalid = useCallback((errors: FieldErrors<{ email: string; password: string }>) => {
    console.warn('[batiweb:signup] formulaire_invalide | ' + JSON.stringify(errors))
  }, [])

  const onRegister = useCallback(
    async (data: { email: string; password: string }) => {
      const domain = data.email.includes('@') ? data.email.split('@')[1]?.toLowerCase() : '?'
      console.log(
        `[batiweb:signup] envoi_firebase | domain=${domain} | pwdLen=${data.password?.length ?? 0}`
      )
      setIsSubmitting(true)
      setError(undefined)
      try {
        await signupManual(data.email, data.password)
        console.log('[batiweb:signup] firebase_ok compte créé / connecté')
      } catch (err: any) {
        const code = err?.code ?? ''
        console.warn(`[batiweb:signup] firebase_erreur | ${code} | ${err?.message ?? err}`)
        const devHint = import.meta.env.DEV && code ? ` [${code}]` : ''
        if (code === 'auth/email-already-in-use') setError('Cet email est déjà utilisé.' + devHint)
        else if (code === 'auth/invalid-email') setError('Email invalide.' + devHint)
        else if (code === 'auth/weak-password') setError('Mot de passe trop faible (6 caractères min).' + devHint)
        else if (code === 'auth/network-request-failed')
          setError('Réseau indisponible ou bloqué. Réessayez ou changez de connexion.' + devHint)
        else if (code === 'auth/operation-not-allowed')
          setError('Inscription e-mail désactivée dans Firebase (Authentication → Sign-in method).' + devHint)
        else setError((err?.message ?? "Erreur lors de l'inscription.") + devHint)
      } finally {
        setIsSubmitting(false)
      }
    },
    [setError]
  )

  return (
    <FormProvider {...formContext}>
      <Box
        component="form"
        noValidate
        onSubmit={formContext.handleSubmit(onRegister, onRegisterInvalid)}
        sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TextFieldElement name="email" placeholder="Adresse email" type="email" fullWidth required sx={fieldSx} variant="outlined" />
        <TextFieldElement
          name="password"
          placeholder="Mot de passe"
          type={showPassword ? 'text' : 'password'}
          fullWidth
          required
          sx={fieldSx}
          variant="outlined"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  size="small"
                  tabIndex={-1}
                  onClick={() => setShowPassword((p) => !p)}
                  sx={{ color: '#94a3b8', '&:hover': { color: '#64748b' } }}>
                  {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        <Box sx={{ mt: 3 }}>
          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            disableElevation
            sx={{
              py: 1.5,
              borderRadius: '24px',
              fontWeight: 600,
              fontSize: '0.95rem',
              textTransform: 'none',
              backgroundColor: '#4277b9',
              '&:hover': { backgroundColor: '#3666a3' }
            }}>
            {isSubmitting ? 'Création…' : 'Créer un compte'}
          </Button>
        </Box>
      </Box>
    </FormProvider>
  )
}
